import { Fragment } from "react";
import { Link, useParams, useSearchParams } from "react-router-dom";

import { useItemCrafting, useItemHistory, useItemOffers } from "../api/queries";
import type { ItemObservation, PricedMode } from "../api/types";
import type { Spread } from "../market";
import "./pages.css";
import { useSession } from "../auth";
import { PriceHistory } from "../chart/PriceHistory";
import { AdminPanel } from "../components/AdminPanel";
import { Empty } from "../components/Empty";
import { ErrorBand } from "../components/ErrorBand";
import { ModePill } from "../components/ModePill";
import { NpcPricePanel } from "../components/NpcPricePanel";
import { Panel } from "../components/Panel";
import { Skeleton } from "../components/Skeleton";
import { TitleBand } from "../components/TitleBand";
import { Variation } from "../components/Variation";
import { WatchStar } from "../components/WatchStar";
import { adena, offerPrice, plural, signed, when } from "../format";
import { recipeHref, traderHref } from "../search/nav";
import { itemQuote, itemSpread, quoteNote, type Quote } from "./itemQuote";
import { variantGroups, variantsWorthShowing } from "./itemVariants";

/** One side of the market: its label, its figure, and how old the figure is.
 *
 *  The age is relative and the exact time is the `title`, the split format.ts's
 *  `ago` prescribes — a headline price raises "is this current?", which only an
 *  age answers at a glance, and "when exactly?", which only a date does.
 *
 *  `mode` doubles as the label and as the class carrying the hue, which is what
 *  keeps the word and the colour from ever disagreeing. The word is doing the
 *  work: DESIGN.md's rule for the mode pills is that buy and sell stay
 *  distinguishable without colour, and a figure is no different. */
function QuoteFigure({ itemId, item, mode, quote, title }: {
  itemId: number; item: string; mode: PricedMode; quote: Quote | null; title?: string;
}) {
  return (
    <div>
      <dt>{title || mode}</dt>
      <dd className={quote === null ? "none" : mode}>{adena(quote?.price ?? null)}</dd>
      <dd className="meta" title={quote === null ? undefined : when(quote.observed_at)}>
        {/* Who to go and trade with. The figure above is one person's offer,
            not an average, and the reader's next move is to find them — which
            without this means scrolling the table below looking for the row
            that price came from.

            Ahead of the note and joined by the same middot quoteNote uses
            internally, so the line reads as one phrase: "Kaelor · 2h ago ·
            best of 5 bids". It is a link, so it is built here rather than
            concatenated into quoteNote, which stays a pure string function.

            Both the nick and its separator go when there is no quote: the line
            is then exactly "never seen", with no punctuation left hanging off
            a trader who does not exist. */}
        {quote === null ? null : (
          <>
            <Link to={traderHref(quote.nick)}>{quote.nick}</Link>
            {" · "}
          </>
        )}
        {quoteNote(quote, mode)}
        {/* Which of the item this is the price of. The table below marks the
            same difference per row; here it matters more, because a lone
            figure has no row beside it to give a "+6" away — and a bid for an
            enchanted one, read as the plain item's, is off by an order of
            magnitude. Renders nothing on nearly every item. */}
        {quote === null ? null : <Variation {...quote} />}
      </dd>
      {/* `item` is the item's name and `title` is this side's label: the star
          needs the former, because "watch" on its own names nothing and there
          are two of these in the one list. */}
      <dd className="meta"><WatchStar itemId={itemId} item={item} mode={mode} /></dd>
    </div>
  );
}

/** The gap between the two figures beside it — the whole reason both of them
 *  are on one line.
 *
 *  Subtracting two headline numbers in your head is exactly the work a page
 *  like this exists to have already done, and it is work the reader gets wrong:
 *  the figures run to eight digits and the interesting answer is a percentage,
 *  not a difference.
 *
 *  Bone, not a hue, and that is a hierarchy rather than an omission. The two
 *  figures beside this one were each observed — a person typed them into a
 *  store window — and they wear the colours that say which side typed them.
 *  This one is arithmetic on both, so it wears the page's text colour. Rose or
 *  olive here would claim it belonged to one of the two sides; gold would put
 *  the loudest ink on the page's least direct reading.
 *
 *  The sign is the reading. `spreadOf` is buy minus sell — `server/lib/
 *  queries.py`'s own definition, "positive when someone pays more than someone
 *  asks" — so this is normally negative, and a positive one is money on the
 *  table. `signed()` prints a real minus rather than a hyphen, which is what
 *  keeps it lined up under the digits above it. */
function SpreadFigure({ spread }: { spread: Spread | null }) {
  return (
    <div>
      <dt>Spread</dt>
      <dd className={spread === null ? "none" : "derived"}>
        {spread === null ? adena(null) : signed(spread.gap)}
      </dd>
      <dd className="meta">
        {/* Degraded honestly rather than filled in. A missing side is not a
            spread of zero — zero would say the market is exactly balanced,
            which is the opposite of "nobody is on one side of it". */}
        {spread === null
          ? "needs a bid and an ask"
          : spread.pct === null
            ? "against a free ask"
            : `${signed(Math.round(spread.pct))}% of the ask`}
      </dd>
    </div>
  );
}

/** The newest offers on one side of this item's market, WTB or WTS.
 *
 *  Two of these rather than the single table that used to sit here. That table
 *  took the newest 25 rows across every mode at once, so an item with a busy
 *  WTS window filled all 25 with asks and the page read as though nobody was
 *  bidding — the one comparison this page exists to support, gone because one
 *  side happened to be noisier. The cap is 25 *per side* and the slice happens
 *  after the filter, which is the entire point of it: each side gets its own
 *  25 and neither can crowd the other off the page.
 *
 *  No Mode column. The band above every one of these rows already says WTB or
 *  WTS, so a pill repeating it 25 times is a column of one word; OtherOffers
 *  below keeps its Mode column because its rows genuinely differ.
 *
 *  The sort is not the in-place hazard it looks like: filter has already
 *  handed back a fresh array, so the caller's observations are never
 *  reordered. */
function RecentOffers({
  mode,
  observations,
  isLoading,
}: {
  mode: PricedMode;
  observations: ItemObservation[];
  isLoading: boolean;
}) {
  const label = mode === "buy" ? "WTB" : "WTS";
  const rows = observations
    .filter((offer) => offer.mode === mode)
    .sort((a, b) => b.observed_at - a.observed_at)
    .slice(0, 25);

  return (
    <>
      <TitleBand title="Recent offers" note={`${label} · newest first`} />
      <Panel>
        {isLoading ? (
          <Skeleton rows={3} />
        ) : rows.length === 0 ? (
          // Said per side, not once for the pair. One window busy while the
          // other is silent is an ordinary state of this market and a useful
          // thing to learn — the old single table could not show it at all,
          // because a quiet side just never appeared among the newest 25.
          <Empty>No {label} offers recorded.</Empty>
        ) : (
          <div className="scroll">
            <table>
              <thead>
                <tr>
                  <th>When</th>
                  <th>Trader</th>
                  <th className="num">Qty</th>
                  <th className="num">Price</th>
                </tr>
              </thead>
              <tbody>
                {rows.map((offer) => (
                  <tr key={offer.id}>
                    <td className="dim">{when(offer.observed_at)}</td>
                    <td>
                      <Link to={traderHref(offer.nick)}>{offer.nick}</Link>
                      {/* Beside the trader rather than in a column of its own:
                          the page is already one item, so this says which of
                          that item they had, and it is blank on nearly every
                          row — a column would be mostly empty width. */}
                      <Variation {...offer} />
                    </td>
                    <td className="num">{adena(offer.qty)}</td>
                    <td className={`num ${mode}`}>{offerPrice(offer)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </Panel>
    </>
  );
}

/** Each distinct good filed under this item's id, with the market it actually
 *  has.
 *
 *  Migration 005 files an enchanted or augmented listing under the *plain*
 *  item's id, so one page's offers can describe several goods trading an order
 *  of magnitude apart. Everything above this panel is the plain item — the
 *  headline figures by `itemQuote`, the chart by `item_history`, both
 *  deliberately — which leaves a weapon whose whole market is +6s with a page
 *  that says nothing about the market it has. This is where it says it.
 *
 *  Absent rather than empty, the rule RecentOffers' twin OtherOffers and the two
 *  admin panels state for themselves. `variantsWorthShowing` is the condition
 *  and carries the argument for it — including why it is not simply "more than
 *  one group", which would hide the panel on the one item that has nothing else.
 *
 *  Olive on the bid and rose on the ask, the hues DESIGN.md spends on WTB and
 *  WTS and on nothing else — the same two colours the tables below wear, so a
 *  reader who has learned them once reads this panel without being told.
 *
 *  No Spread column. The gap between a bid and an ask is a live market's
 *  reading, and these two sides routinely come from different scans of a good
 *  nobody trades often; `SpreadFigure` above earns its subtraction by being
 *  about the item the whole page is about. */
function VariantMarkets({ observations }: { observations: ItemObservation[] }) {
  const groups = variantGroups(observations);
  if (!variantsWorthShowing(groups)) return null;

  return (
    <>
      <TitleBand
        title="Variants"
        note={`${plural(groups.length, "good")} under one item id`}
      />
      <Panel>
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>Variant</th>
                <th className="num">Best buy</th>
                <th className="num">Best sell</th>
                <th className="num">Offers</th>
              </tr>
            </thead>
            <tbody>
              {groups.map((group) => (
                <tr key={group.key}>
                  {/* Dimmed on the plain row and only there. "plain" is the
                      absence of a variation, so it should not have the weight
                      of a "+6"; it is also the row a reader coming down from
                      the headline figures already knows, and dimming it points
                      the eye at the ones they do not. */}
                  <td className={group.enchant || group.element || group.ability
                    ? undefined : "dim"}>
                    {group.label}
                  </td>
                  <td className="num buy">{adena(group.buy?.price ?? null)}</td>
                  <td className="num sell">{adena(group.sell?.price ?? null)}</td>
                  {/* Depth, across every scan the page holds — not the quoted
                      scan's count. Two sightings of a +6 is one trader who has
                      stood there twice, and that is the difference between a
                      price and a market. */}
                  <td className="num dim">{group.offers}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
}

/** Craft fees and package lots — everything the offers request returns that is
 *  neither a bid nor an ask.
 *
 *  useItemOffers asks for no mode and query.py serves all four, so these rows
 *  reach this page whether or not anything renders them. Splitting the table
 *  into WTB and WTS alone would have dropped them silently, and they are real
 *  readings somebody walked a window for: what a crafter charges to make this,
 *  and the lots it has been bundled into.
 *
 *  Absent rather than empty when there are none — the rule the Crafting panel
 *  and the two admin panels below state for themselves. On the large majority
 *  of items nobody has ever crafted or bundled this one, and on those pages
 *  this panel simply is not there.
 *
 *  It keeps the Mode column the two panels above dropped: this is the one
 *  table on the page that mixes two modes, so the pill is still doing work.
 *  The price goes through offerPrice for the same reason — a package row has
 *  no price of its own, only the lot's, and offerPrice is what marks it. */
function OtherOffers({ observations }: { observations: ItemObservation[] }) {
  const rows = observations
    .filter((offer) => offer.mode !== "buy" && offer.mode !== "sell")
    .sort((a, b) => b.observed_at - a.observed_at)
    .slice(0, 25);
  if (rows.length === 0) return null;

  return (
    <>
      <TitleBand title="Recent offers" note="fees and lots · newest first" />
      <Panel>
        <div className="scroll">
          <table>
            <thead>
              <tr>
                <th>When</th>
                <th>Mode</th>
                <th>Trader</th>
                <th className="num">Qty</th>
                <th className="num">Price</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((offer) => (
                <tr key={offer.id}>
                  <td className="dim">{when(offer.observed_at)}</td>
                  <td><ModePill mode={offer.mode} /></td>
                  <td>
                    <Link to={traderHref(offer.nick)}>{offer.nick}</Link>
                    <Variation {...offer} />
                  </td>
                  <td className="num">{adena(offer.qty)}</td>
                  <td className="num">{offerPrice(offer)}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Panel>
    </>
  );
}

export function Item() {
  const { id = "" } = useParams();
  const [params] = useSearchParams();
  const itemId = Number(id);
  const { me } = useSession();
  const history = useItemHistory(itemId, null);
  const offers = useItemOffers(itemId);
  const crafting = useItemCrafting(itemId);
  // The API has no id-to-name route, so the name rides in the URL and survives a
  // copied link. Absent — someone typed the path — the id is at least honest.
  const title = params.get("name") ?? `Item #${itemId}`;

  // Not Number.isNaN: Number("") is 0, and Number("3.5") is 3.5. Neither is an
  // item id, and both sailed through the old guard into a doomed request.
  if (!Number.isInteger(itemId) || itemId <= 0) {
    return (
      <>
        <TitleBand title="Item" />
        <ErrorBand message="That is not an item." hint="Open an item from a scan's table." />
      </>
    );
  }
  if (history.isLoading) return <><TitleBand title="Item" /><Skeleton rows={6} /></>;
  if (history.isError) {
    return (
      <>
        <TitleBand title="Item" />
        <ErrorBand message="Could not load this item's history." hint="Reload in a moment." />
      </>
    );
  }

  const points = history.data ?? [];
  const observations = offers.data?.offers ?? [];
  const quote = itemQuote(observations);
  // Derived here as well as inside the panel, because the chart's empty state
  // needs the same answer: a chart with no points over an item that plainly has
  // offers is an item nobody listed plain, and the Empty below has to say which
  // of the two silences it is.
  const variants = variantGroups(observations);
  // Days, not rows. `points.length` counted one row per bucket PER MODE, so a
  // month of two-sided trading announced itself as "56 day-buckets" above a
  // chart plainly 28 days wide — and the chart's own aria-label, which counts
  // distinct buckets, said 28 at the same time.
  const scanned = new Set(points.map((p) => p.bucket)).size;

  return (
    <>
      <TitleBand title={title} note={`${plural(scanned, "day")} scanned`} />
      <Panel>
        {/* What the item trades at now, above the chart rather than 25 rows
            down the table below it — which is the whole reason it is here.

            It rides the offers request, not the history one, so it is a
            skeleton while that is in flight and absent if it fails: the error
            band under Recent offers names both absences rather than this spot
            growing an error of its own. Absent too for an item nobody has
            traded, where the Empty below already says so and a pair of dashes
            would only say it again, larger. */}
        {offers.isLoading ? (
          <Skeleton rows={2} />
        ) : offers.isError || observations.length === 0 ? null : (
          <dl className="quote-summary">
            <QuoteFigure itemId={itemId} item={title} title="WTB" mode="buy" quote={quote.buy} />
            <QuoteFigure itemId={itemId} item={title} title="WTS" mode="sell" quote={quote.sell} />
            <SpreadFigure spread={itemSpread(quote)} />
          </dl>
        )}
        {points.length === 0 ? (
          // Two different silences, and the old wording told the wrong one.
          // The chart is the plain item — `item_history` filters to it, and the
          // legend note says so — so an item whose every listing was enchanted
          // or augmented has no points and plenty of offers. "No offers
          // recorded for this item yet" was simply false there, and it sent a
          // reader away from a page holding the market they came for.
          <Empty>
            {variantsWorthShowing(variants)
              ? "Nobody has listed this item plain, and the chart is the plain " +
                "item. The variants below have prices of their own."
              : "No offers recorded for this item yet."}
          </Empty>
        ) : (
          <PriceHistory points={points} offers={observations} />
        )}
      </Panel>

      {/* After the headline figures and the chart, and above the per-side
          tables: it answers the question those two raise on a weapon — "whose
          price is that, and what about the +6?" — and it answers it before the
          reader starts scrolling 25 rows looking for the answer themselves. */}
      <VariantMarkets observations={observations} />

      {/* One band for a failed request, not one per panel: the split below is
          three views of a single fetch, and reporting the same failure two or
          three times over would read as three things having gone wrong.

          Without this branch a failed fetch leaves observations empty, which
          renders as two "No offers recorded." panels and silently takes the
          scatter out of the chart above — indistinguishable from an item
          nobody has traded. */}
      {offers.isError ? (
        <>
          <TitleBand title="Recent offers" />
          <Panel>
            <ErrorBand
              message="Could not load this item's offers."
              hint={
                "The price history above still loaded; the headline prices and the " +
                "per-offer scatter are missing for the same reason."
              }
            />
          </Panel>
        </>
      ) : (
        <>
          <RecentOffers mode="buy" observations={observations} isLoading={offers.isLoading} />
          <RecentOffers mode="sell" observations={observations} isLoading={offers.isLoading} />
          <OtherOffers observations={observations} />
        </>
      )}

      {/* Absent entirely for an item no recipe mentions, which is most of them.
          An empty panel would be a permanent question the page cannot answer.
          A failed fetch is silence too: this is a cross-reference, and taking
          the price history down for it would be the wrong trade. */}
      {crafting.data &&
      (crafting.data.makes.length > 0 ||
        crafting.data.used_in.length > 0 ||
        crafting.data.recipe_for.length > 0) ? (
        <>
          <TitleBand title="Crafting" note="what makes this, what it makes, and what it teaches" />
          <Panel>
            {crafting.data.makes.length > 0 ? (
              <p className="scan-meta">
                {/* One span, not bare siblings: .scan-meta is a flex container,
                    so a bare comma between two <Link> siblings becomes its own
                    anonymous flex item and the flex gap lands on both sides of
                    it. Wrapping the whole sentence in one element keeps the
                    comma inside ordinary inline text instead. */}
                <span>
                  Made by{" "}
                  {crafting.data.makes.map((link, index) => (
                    <Fragment key={link.recipe_id}>
                      {index > 0 ? ", " : ""}
                      <Link to={recipeHref(link.recipe_id)}>{link.name}</Link>
                    </Fragment>
                  ))}
                </span>
              </p>
            ) : null}
            {crafting.data.used_in.length > 0 ? (
              <p className="scan-meta">
                <span>
                  Used in{" "}
                  {crafting.data.used_in.map((link, index) => (
                    <Fragment key={link.recipe_id}>
                      {index > 0 ? ", " : ""}
                      <Link to={recipeHref(link.recipe_id)}>{link.craft_item}</Link>
                    </Fragment>
                  ))}
                </span>
              </p>
            ) : null}
            {/* This item is a recipe scroll. The crafted item's name is the
                link text, not the scroll's own — the reader is already on the
                scroll's page, and what they want to know is what it makes.
                Several rows when one scroll teaches several recipes: 13 names
                in the catalogue do, every potion among them.

                `scroll_consumed` says once, for the whole line, what
                `used_in` no longer repeats per recipe: on this server a
                craft burns its scroll, so on 610 of these pages the scroll
                is also one of the recipe's own materials. */}
            {crafting.data.recipe_for.length > 0 ? (
              <p className="scan-meta">
                <span>
                  Recipe for{" "}
                  {crafting.data.recipe_for.map((link, index) => (
                    <Fragment key={link.recipe_id}>
                      {index > 0 ? ", " : ""}
                      <Link to={recipeHref(link.recipe_id)}>{link.craft_item}</Link>
                    </Fragment>
                  ))}
                  {crafting.data.scroll_consumed ? ", which consumes it" : ""}
                </span>
              </p>
            ) : null}
          </Panel>
        </>
      ) : null}

      {/* uploader and owner, never a viewer — absent rather than disabled, the
          same rule the owner-only admin panel below follows. A control a
          reader cannot use is a question the page cannot answer.

          Gated on the fetch as well as the role, the way everything else on
          this page is gated. The price rides /crafting, so it is missing while
          that request is in flight and missing again if it fails — and the
          panel renders a missing price as "No vendor price recorded." above a
          Set button whose PUT upserts. Ungated, it invites an uploader to type
          over a price they were never shown. */}
      {me?.role === "uploader" || me?.role === "owner" ? (
        crafting.isLoading ? (
          <Skeleton rows={2} />
        ) : crafting.isError ? (
          <ErrorBand
            message="Could not load this item's vendor price."
            hint="The panel is hidden rather than offering to overwrite a price it could not read."
          />
        ) : crafting.data ? (
          <NpcPricePanel itemId={itemId} price={crafting.data.npc_price ?? null} />
        ) : null
      ) : null}

      {/* No name is passed here: the panel reads the authoritative name from
          the server's impact endpoint, precisely because this page's heading
          comes from a ?name= query string that a hand-typed URL will not
          carry — and confirming a destructive act against a name the page
          merely guessed is not acceptable. */}
      {me?.role === "owner" ? (
        <AdminPanel kind="items" id={itemId} />
      ) : null}
    </>
  );
}
